import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { X } from 'lucide-react'
import { useOrganizationTeams } from '../hooks/use-organization-teams'
import { useUpdateUserTeams } from '../hooks/use-update-user-teams'
import { cn } from '@/shared/lib/utils'

interface EditUserTeamsModalProps {
  isOpen: boolean
  onClose: () => void
  userId: string
  userName: string
  currentTeamIds: string[]
}

export function EditUserTeamsModal({
  isOpen,
  onClose,
  userId,
  userName,
  currentTeamIds,
}: EditUserTeamsModalProps) {
  const { t } = useTranslation()
  const { data: teams = [] } = useOrganizationTeams()
  const updateUserTeams = useUpdateUserTeams()

  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>(currentTeamIds)

  // Reset selection when modal opens with new user
  useEffect(() => {
    if (isOpen) {
      setSelectedTeamIds(currentTeamIds)
    }
  }, [isOpen, currentTeamIds])

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeamIds(prev =>
      prev.includes(teamId)
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      await updateUserTeams.mutateAsync({
        userId,
        teamIds: selectedTeamIds,
      })
      toast.success(t('settings.team.teamsUpdated'))
      onClose()
    } catch {
      toast.error(t('common.error'))
    }
  }

  const handleClose = () => {
    setSelectedTeamIds(currentTeamIds)
    onClose()
  }

  if (!isOpen) return null

  const hasChanges = JSON.stringify([...selectedTeamIds].sort()) !== JSON.stringify([...currentTeamIds].sort())

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md rounded-xl bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {t('settings.team.editTeams.title')}
            </h2>
            <p className="mt-0.5 text-sm text-foreground-muted">
              {t('settings.team.editTeams.subtitle', { name: userName })}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-muted transition-colors hover:bg-background-secondary hover:text-foreground"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Content */}
          <div className="p-6">
            {teams.length === 0 ? (
              <p className="text-sm text-foreground-muted text-center py-4">
                {t('settings.team.editTeams.noTeamsAvailable')}
              </p>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {teams.map((team) => (
                  <label
                    key={team.id}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer transition-colors',
                      selectedTeamIds.includes(team.id)
                        ? 'bg-accent/10'
                        : 'hover:bg-background-secondary'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTeamIds.includes(team.id)}
                      onChange={() => handleTeamToggle(team.id)}
                      className="h-4 w-4 rounded border-border text-accent focus:ring-accent"
                    />
                    <span className="text-sm font-medium text-foreground">{team.name}</span>
                  </label>
                ))}
              </div>
            )}

            <p className="mt-4 text-xs text-foreground-muted">
              {t('settings.team.editTeams.hint')}
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-background-secondary transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={updateUserTeams.isPending || !hasChanges}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90 transition-colors disabled:opacity-50"
            >
              {updateUserTeams.isPending
                ? t('settings.team.editTeams.saving')
                : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
